import React, { useState, useEffect, useCallback, useMemo, useRef, useReducer } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from './Layout';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { publicApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { MembersOnlyGate } from './marketplace/MembersOnlyGate';
import { MerchantDealCard } from './marketplace/MerchantDealCard';
import { DealChips } from './marketplace/DealChips';
import { JoinStrip, useMarketplaceStats } from './marketplace/JoinStrip';
import { viewerTier } from './marketplace/dealUtils';
import { DualPrice } from './DualPrice';
import { TIER_STYLES, tierKey, tierLabel } from '../utils/tierStyles';
import {
  Store, Search, Loader2, MapPin, ShoppingBag, ChevronRight,
  ChevronLeft, Building2, ArrowRight, Tag, Package, Wrench,
  SlidersHorizontal, Locate, X, Navigation, Home
} from 'lucide-react';

// ─── Reusable image fallbacks (state-based, not innerHTML hacks) ──────────

// Tier → coloured glow around the merchant logo tile. Falls back to the
// existing cyan/emerald gradient (no glow) when the merchant has no
// active subscription. Styling is shared with the merchant detail page
// and item cards via ../utils/tierStyles.

function ItemImage({ src, alt, itemType }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    const Icon = itemType === 'product' ? Package : Wrench;
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#041A3B] to-[#0A254D] flex items-center justify-center" data-testid="item-image-fallback">
        <Icon className="w-12 h-12 text-[#5C7599]/30" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      onError={() => setErrored(true)}
      data-testid="item-image-img"
    />
  );
}

export function MerchantPartnersPage() {
  const [tab, setTab] = useState('merchants'); // merchants | products | services
  const searchInputRef = useRef(null);
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // ── Consolidated filter state (was 8 separate useState calls) ───────────
  // Reducer collapses all marketplace filters into a single object so the
  // dependency arrays on fetchData / activeFilterCount drop from 11 / 7
  // down to 1, and we get a free "reset all" action.
  const INITIAL_FILTERS = {
    search: '', industryFilter: '', categoryFilter: '',
    minPrice: '', maxPrice: '', discountOnly: false,
    sort: 'relevance', radiusKm: '', tierFilter: '',
  };
  const filtersReducer = (s, a) => {
    switch (a.type) {
      case 'set':   return { ...s, [a.key]: a.value };
      case 'reset': return { ...INITIAL_FILTERS, sort: s.sort };  // keep current sort
      default:      return s;
    }
  };
  const [filters, dispatchFilter] = useReducer(filtersReducer, INITIAL_FILTERS);
  const { search, industryFilter, categoryFilter, minPrice, maxPrice, discountOnly, sort, radiusKm, tierFilter } = filters;

  // Stable setters — referentially constant across renders so callers
  // (onChange, child memos) don't re-render unnecessarily.
  const setSearch         = useCallback(v => dispatchFilter({ type: 'set', key: 'search',         value: v }), []);
  const setIndustryFilter = useCallback(v => dispatchFilter({ type: 'set', key: 'industryFilter', value: v }), []);
  const setCategoryFilter = useCallback(v => dispatchFilter({ type: 'set', key: 'categoryFilter', value: v }), []);
  const setMinPrice       = useCallback(v => dispatchFilter({ type: 'set', key: 'minPrice',       value: v }), []);
  const setMaxPrice       = useCallback(v => dispatchFilter({ type: 'set', key: 'maxPrice',       value: v }), []);
  const setDiscountOnly   = useCallback(v => dispatchFilter({ type: 'set', key: 'discountOnly',   value: v }), []);
  const setSort           = useCallback(v => dispatchFilter({ type: 'set', key: 'sort',           value: v }), []);
  const setTierFilter     = useCallback(v => dispatchFilter({ type: 'set', key: 'tierFilter',     value: v }), []);
  const setRadiusKm       = useCallback(v => dispatchFilter({ type: 'set', key: 'radiusKm',       value: v }), []);

  // Geolocation
  const [userLoc, setUserLoc] = useState(null);            // { lat, lng } | null
  const [locStatus, setLocStatus] = useState('idle');      // idle | locating | error | granted
  const [locSource, setLocSource] = useState(null);        // 'gps' | 'profile' | null

  // Authenticated user's saved address (for "Use my saved address" option).
  const { user, isAuthenticated, api, loading: authLoading } = useAuth();
  const myTier = viewerTier(user);
  const mpStats = useMarketplaceStats();
  const profileLoc = useMemo(() => {
    const a = user?.address;
    if (a && typeof a === 'object'
        && typeof a.latitude === 'number'
        && typeof a.longitude === 'number') {
      return {
        lat: a.latitude,
        lng: a.longitude,
        label: [a.city, a.country].filter(Boolean).join(', '),
      };
    }
    return null;
  }, [user]);

  const PER_PAGE_OPTIONS = [12, 24, 48, 96];
  const [limit, setLimit] = useState(() => {
    const saved = parseInt(localStorage.getItem('aga_marketplace_per_page') || '12', 10);
    return PER_PAGE_OPTIONS.includes(saved) ? saved : 12;
  });
  useEffect(() => {
    localStorage.setItem('aga_marketplace_per_page', String(limit));
  }, [limit]);
  const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 2 }).format(n || 0);
  const industryName = (id) => industries.find(i => i.id === id)?.name || '';

  // Persist user location across page refreshes/sessions
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('aga_user_loc') || 'null');
      if (cached && typeof cached.lat === 'number' && typeof cached.lng === 'number') {
        setUserLoc(cached);
        setLocStatus('granted');
        setLocSource(cached.source || 'gps');
        return;
      }
    } catch (_) {/* ignore */}
    // If no cached location, fall back to the authenticated user's saved
    // profile address. This makes "near me" results show up immediately on
    // marketplace load without requiring an OS geolocation permission prompt.
    if (profileLoc) {
      setUserLoc({ lat: profileLoc.lat, lng: profileLoc.lng });
      setLocSource('profile');
      setLocStatus('granted');
    }
  }, [profileLoc]);

  // Debounce search input
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearch(searchInput), 350);
    return () => searchTimerRef.current && clearTimeout(searchTimerRef.current);
  }, [searchInput]);

  // Fetch industries on mount
  useEffect(() => {
    publicApi.get('/api/marketplace/industries')
      .then(({ data }) => setIndustries(data.industries || []))
      .catch(() => {});
  }, []);

  // Fetch categories when tab changes (products/services)
  useEffect(() => {
    if (tab === 'merchants') { setCategoryFilter(''); return; }
    const type = tab === 'products' ? 'product' : 'service';
    publicApi.get(`/api/marketplace/categories?type=${type}`)
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
    setCategoryFilter('');
  }, [tab]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [tab, search, industryFilter, categoryFilter, minPrice, maxPrice, discountOnly, sort, radiusKm, userLoc, limit]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocStatus('error');
      return;
    }
    setLocStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLoc(loc);
        setLocStatus('granted');
        setLocSource('gps');
        try { localStorage.setItem('aga_user_loc', JSON.stringify({ ...loc, source: 'gps' })); } catch (_) {/* ignore */}
        if (sort === 'relevance') setSort('distance');
      },
      () => setLocStatus('error'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, [sort]);

  const useProfileLocation = useCallback(() => {
    if (!profileLoc) return;
    setUserLoc({ lat: profileLoc.lat, lng: profileLoc.lng });
    setLocStatus('granted');
    setLocSource('profile');
    try { localStorage.setItem('aga_user_loc', JSON.stringify({ lat: profileLoc.lat, lng: profileLoc.lng, source: 'profile' })); } catch (_) {/* ignore */}
    if (sort === 'relevance') setSort('distance');
  }, [profileLoc, sort]);

  const clearLocation = useCallback(() => {
    setUserLoc(null);
    setLocStatus('idle');
    setLocSource(null);
    setRadiusKm('');
    if (sort === 'distance') setSort('relevance');
    try { localStorage.removeItem('aga_user_loc'); } catch (_) {/* ignore */}
  }, [sort]);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const baseParams = new URLSearchParams({ page, limit });
      if (userLoc) {
        baseParams.append('lat', userLoc.lat);
        baseParams.append('lng', userLoc.lng);
        if (filters.radiusKm) baseParams.append('radius_km', filters.radiusKm);
      }

      if (tab === 'merchants') {
        if (filters.search) baseParams.append('search', filters.search);
        if (filters.industryFilter) baseParams.append('industry_id', filters.industryFilter);
        const merchantSort = userLoc && (filters.sort === 'distance' || filters.sort === 'relevance') ? 'distance'
          : ({ newest: 'newest', relevance: 'featured', discount: 'discount' }[filters.sort] || 'name');
        baseParams.append('sort', merchantSort);
        if (filters.tierFilter) baseParams.append('deal', filters.tierFilter);
        const { data } = await api.get(`/api/merchant-partners/?${baseParams}`);
        setMerchants(data.merchants || []);
        setTotal(data.total || 0);
      } else {
        const type = tab === 'products' ? 'product' : 'service';
        baseParams.append('type', type);
        if (filters.search) baseParams.append('q', filters.search);
        if (filters.industryFilter) baseParams.append('industry_id', filters.industryFilter);
        if (filters.categoryFilter) baseParams.append('category_id', filters.categoryFilter);
        if (filters.minPrice !== '') baseParams.append('min_price', filters.minPrice);
        if (filters.maxPrice !== '') baseParams.append('max_price', filters.maxPrice);
        if (filters.discountOnly) baseParams.append('discount_only', 'true');
        const itemSort = userLoc && filters.sort === 'relevance' ? 'distance' : filters.sort;
        baseParams.append('sort', itemSort);

        const { data } = await api.get(`/api/marketplace/items?${baseParams}`);
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch:', e);
    }
    setLoading(false);
  }, [tab, page, limit, userLoc, filters, isAuthenticated, api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(total / limit);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (filters.industryFilter) c++;
    if (filters.categoryFilter) c++;
    if (filters.minPrice !== '') c++;
    if (filters.maxPrice !== '') c++;
    if (filters.discountOnly) c++;
    if (filters.radiusKm) c++;
    if (filters.sort !== 'relevance') c++;
    return c;
  }, [filters]);

  const clearAllFilters = () => {
    dispatchFilter({ type: 'reset' });
    setSort('relevance');
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const TabBtn = ({ value, label, icon: Icon }) => (
    <button
      onClick={() => setTab(value)}
      data-testid={`tab-${value}`}
      className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
        tab === value
          ? 'bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/40'
          : 'bg-[#041A3B] text-[#A0B4CC] border border-white/10 hover:border-white/30'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  if (!authLoading && !isAuthenticated) return <MembersOnlyGate />;

  return (
    <MarketplaceShell>
    <main className="pt-20 pb-24 sm:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="font-outfit text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-2 sm:mb-3" data-testid="marketplace-title">
              AGA Marketplace
            </h1>
            <p className="text-[#A0B4CC] text-sm sm:text-base max-w-2xl mx-auto px-2">
              Exclusive member discounts at trusted partner merchants. Show your AGA card, pay less.
            </p>
            {mpStats && (
              <p className="text-xs sm:text-sm text-[#5C7599] mt-2" data-testid="marketplace-social-proof">
                <b className="text-white">{mpStats.merchants.toLocaleString()}</b> partner merchants ·{' '}
                <b className="text-white">{mpStats.members.toLocaleString()}</b> active members
                {mpStats.total_saved > 0 && <> · <b className="text-[#00E676]">₱{Math.round(mpStats.total_saved).toLocaleString()}</b> saved by members</>}
              </p>
            )}
          </div>

          {!myTier && <JoinStrip stats={mpStats} loggedIn={!!user} />}

          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4" data-testid="marketplace-tabs">
            <TabBtn value="merchants" label="Merchants" icon={Store} />
            <TabBtn value="products" label="Products" icon={Package} />
            <TabBtn value="services" label="Services" icon={Wrench} />
          </div>

          {/* Search + Geo */}
          <div className="flex flex-col gap-3 mb-3 max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
                <Input
                  ref={searchInputRef}
                  placeholder={tab === 'merchants' ? 'Search merchants, locations…' : `Search ${tab} by name, tag, or category…`}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-10 bg-[#041A3B] border-white/10 text-white h-11"
                  data-testid="marketplace-search"
                />
                {searchInput && (
                  <button
                    onClick={() => { setSearchInput(''); setSearch(''); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C7599] hover:text-white"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(s => !s)}
                  className={`h-11 border-white/10 text-white relative flex-1 sm:flex-none ${showFilters ? 'bg-white/5' : 'bg-[#041A3B]'}`}
                  data-testid="toggle-filters-btn"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#00E5FF] text-[#000B18] text-[10px] font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
                {/* Authenticated user with a geocoded profile address: offer
                    a "saved address" shortcut alongside the GPS button. */}
                {isAuthenticated && profileLoc && locSource !== 'profile' && (
                  <Button
                    variant="outline"
                    onClick={useProfileLocation}
                    className="h-11 border-white/10 bg-[#041A3B] text-white flex-1 sm:flex-none"
                    data-testid="use-profile-location-btn"
                    title={profileLoc.label ? `Show results near ${profileLoc.label}` : 'Show results near your saved address'}
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Near my address
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={userLoc ? clearLocation : requestLocation}
                  className={`h-11 border-white/10 ${userLoc ? 'bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40' : 'bg-[#041A3B] text-white'} flex-1 sm:flex-none`}
                  data-testid="geo-toggle-btn"
                  disabled={locStatus === 'locating'}
                >
                  {locStatus === 'locating' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Locate className="w-4 h-4 mr-2" />}
                  {userLoc ? 'Nearby ON' : 'Find Nearby'}
                </Button>
              </div>
            </div>

            {/* Location status hints */}
            {locStatus === 'error' && (
              <p className="text-[#FF1744] text-xs px-1">Location permission denied. You can still search by city in the search bar.</p>
            )}
            {userLoc && (
              <div className="flex items-center justify-between text-xs text-[#A0B4CC] px-1" data-testid="location-status">
                <span className="inline-flex items-center gap-1.5">
                  {locSource === 'profile' ? <Home className="w-3 h-3 text-[#00E676]" /> : <Navigation className="w-3 h-3 text-[#00E676]" />}
                  {locSource === 'profile' && profileLoc?.label
                    ? <>Showing results near <span className="text-white">{profileLoc.label}</span> <span className="text-[#5C7599]">(from your profile)</span></>
                    : 'Showing results near you'}
                </span>
                <div className="flex items-center gap-3">
                  {locSource === 'gps' && profileLoc && (
                    <button
                      onClick={useProfileLocation}
                      className="text-[#00E5FF] hover:underline"
                      data-testid="switch-to-profile-loc-btn"
                    >
                      Use saved address
                    </button>
                  )}
                  <button onClick={clearLocation} className="text-[#5C7599] hover:text-white">Clear</button>
                </div>
              </div>
            )}
          </div>

          {/* Filters panel */}
          {showFilters && (
            <div className="bg-[#041A3B] border border-white/10 rounded-lg p-4 sm:p-5 mb-6 max-w-4xl mx-auto" data-testid="filters-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#A0B4CC] mb-1.5">Industry</label>
                  <select
                    value={industryFilter}
                    onChange={e => setIndustryFilter(e.target.value)}
                    className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/10 text-white text-sm"
                    data-testid="industry-filter"
                  >
                    <option value="">All Industries</option>
                    {industries.map(ind => <option key={ind.id} value={ind.id}>{ind.name}</option>)}
                  </select>
                </div>

                {tab !== 'merchants' && categories.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-[#A0B4CC] mb-1.5">Category</label>
                    <select
                      value={categoryFilter}
                      onChange={e => setCategoryFilter(e.target.value)}
                      className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/10 text-white text-sm"
                      data-testid="category-filter"
                    >
                      <option value="">All Categories</option>
                      {categories.map(c => (
                        <optgroup key={c.id} label={c.name}>
                          <option value={c.id}>{c.name} (all)</option>
                          {(c.children || []).map(sub => (
                            <option key={sub.id} value={sub.id}>↳ {sub.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-[#A0B4CC] mb-1.5">Sort by</label>
                  <select
                    value={sort}
                    onChange={e => setSort(e.target.value)}
                    className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/10 text-white text-sm"
                    data-testid="sort-select"
                  >
                    <option value="relevance">Relevance</option>
                    {userLoc && <option value="distance">Nearest first</option>}
                    {tab !== 'merchants' && <option value="price_asc">Price: low to high</option>}
                    {tab !== 'merchants' && <option value="price_desc">Price: high to low</option>}
                    <option value="discount">Biggest discount</option>
                    <option value="newest">Newest</option>
                  </select>
                </div>

                {tab !== 'merchants' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-[#A0B4CC] mb-1.5">Min price (PHP)</label>
                      <Input
                        type="number" inputMode="decimal" min="0"
                        value={minPrice} onChange={e => setMinPrice(e.target.value)}
                        placeholder="0"
                        className="h-10 bg-[#000B18] border-white/10 text-white text-sm"
                        data-testid="min-price-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#A0B4CC] mb-1.5">Max price (PHP)</label>
                      <Input
                        type="number" inputMode="decimal" min="0"
                        value={maxPrice} onChange={e => setMaxPrice(e.target.value)}
                        placeholder="No limit"
                        className="h-10 bg-[#000B18] border-white/10 text-white text-sm"
                        data-testid="max-price-input"
                      />
                    </div>
                  </>
                )}

                {userLoc && (
                  <div>
                    <label className="block text-xs font-medium text-[#A0B4CC] mb-1.5">Within radius</label>
                    <select
                      value={radiusKm}
                      onChange={e => setRadiusKm(e.target.value)}
                      className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/10 text-white text-sm"
                      data-testid="radius-select"
                    >
                      <option value="">Any distance</option>
                      <option value="5">5 km</option>
                      <option value="10">10 km</option>
                      <option value="25">25 km</option>
                      <option value="50">50 km</option>
                      <option value="100">100 km</option>
                      <option value="500">500 km</option>
                    </select>
                  </div>
                )}
              </div>

              {tab !== 'merchants' && (
                <label className="mt-4 inline-flex items-center gap-2 text-sm text-[#A0B4CC] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={discountOnly}
                    onChange={e => setDiscountOnly(e.target.checked)}
                    className="accent-[#00E676]"
                    data-testid="discount-only-toggle"
                  />
                  Show only items with member discounts
                </label>
              )}

              {activeFilterCount > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="text-xs text-[#5C7599]">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied</span>
                  <Button
                    size="sm" variant="ghost"
                    onClick={clearAllFilters}
                    className="text-[#00E5FF] hover:text-white text-xs"
                    data-testid="clear-filters-btn"
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
            </div>
          ) : tab === 'merchants' ? (
            <>
              <DealChips value={tierFilter} onChange={setTierFilter} />
              {merchants.length === 0 ? (
                <EmptyState search={search} title="No Merchants Found" icon={Store} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {merchants.map((m) => (
                    <MerchantDealCard key={m.id} m={m} tier={myTier} loggedIn={!!user} industry={industryName(m.industry_id) || m.business_type}
                      distance={m.distance_km != null ? fmtDistance(m.distance_km) : null} />
                  ))}
                </div>
              )}
            </>
          ) : (
            // Products / Services tab
            items.length === 0 ? (
              <EmptyState search={search} title={`No ${tab} Found`} icon={tab === 'products' ? Package : Wrench} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {items.map(item => (
                  <Link
                    key={item.id} to={`/marketplace/${item.merchant_slug || item.merchant_id}`}
                    className="group" data-testid={`item-card-${item.id}`}
                  >
                    <Card className="bg-[#041A3B] border-white/10 hover:border-[#00E5FF]/40 transition-all duration-300 h-full overflow-hidden">
                      <div className="h-36 sm:h-40 overflow-hidden bg-[#000B18] relative">
                        <ItemImage src={item.image_url} alt={item.name} itemType={item.item_type} />
                        {item.distance_km != null && (
                          <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[#00E5FF] border border-[#00E5FF]/30">
                            <Navigation className="w-3 h-3" /> {fmtDistance(item.distance_km)}
                          </span>
                        )}
                      </div>
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-outfit text-sm sm:text-base font-semibold text-white group-hover:text-[#00E5FF] transition-colors line-clamp-2 flex-1">
                            {item.name}
                          </h3>
                          {item.discount_percentage > 0 && (
                            <span className="bg-[#FF1744]/20 text-[#FF1744] text-[10px] font-bold rounded px-1.5 py-0.5 whitespace-nowrap">
                              -{item.discount_percentage}%
                            </span>
                          )}
                        </div>
                        <p className="text-[#5C7599] text-xs mb-1 flex items-center gap-1 truncate">
                          <Store className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{item.merchant_business_name || item.merchant_name}</span>
                          {(() => {
                            const itemTierStyle = TIER_STYLES[tierKey(item.merchant_subscription_plan)];
                            return itemTierStyle ? (
                              <span
                                className={`inline-flex items-center text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ml-auto flex-shrink-0 ${itemTierStyle.labelBg}`}
                                data-testid={`item-tier-badge-${item.id}`}
                              >
                                {tierLabel(item.merchant_subscription_plan) || itemTierStyle.label}
                              </span>
                            ) : null;
                          })()}
                        </p>
                        {item.category && (
                          <p className="text-[#5C7599] text-xs mb-3 flex items-center gap-1">
                            <Tag className="w-3 h-3" /> {item.category}
                          </p>
                        )}
                        <div className="flex items-end justify-between gap-2 pt-3 border-t border-white/5">
                          <div>
                            {item.member_price < item.regular_price && (
                              <p className="text-[#5C7599] text-[10px] sm:text-xs line-through">
                                <DualPrice php={item.regular_price} usdClassName="opacity-70" />
                              </p>
                            )}
                            <p className="text-[#00E676] text-base sm:text-lg font-bold font-outfit">
                              <DualPrice php={item.member_price} usdClassName="text-xs text-[#A0B4CC] font-medium ml-1" />
                            </p>
                          </div>
                          <span className="text-[#00E5FF] text-xs flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            View <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-8" data-testid="marketplace-pagination">
              <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
                <p className="text-[#5C7599] text-sm">
                  {(() => {
                    const start = total === 0 ? 0 : (page - 1) * limit + 1;
                    const end = Math.min(page * limit, total);
                    return `Showing ${start}–${end} of ${total} ${tab === 'merchants' ? 'merchants' : 'items'}`;
                  })()}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="per-page-select" className="text-[#5C7599] text-sm">Per page:</label>
                  <select
                    id="per-page-select"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                    className="bg-[#041A3B] border border-white/10 text-white text-sm rounded-md px-2 py-1 focus:outline-none focus:border-[#00E5FF]/40"
                    data-testid="per-page-select"
                  >
                    {PER_PAGE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1 flex-wrap justify-center">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    className="text-[#A0B4CC] hover:text-white hover:bg-white/5 disabled:opacity-30"
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </Button>
                  {(() => {
                    // Build page list with ellipses: 1 ... p-1, p, p+1 ... last
                    const pages = [];
                    const add = (n) => pages.push(n);
                    const last = totalPages;
                    const window = 1; // pages around current
                    add(1);
                    const left = Math.max(2, page - window);
                    const right = Math.min(last - 1, page + window);
                    if (left > 2) pages.push('left-ellipsis');
                    for (let i = left; i <= right; i++) add(i);
                    if (right < last - 1) pages.push('right-ellipsis');
                    if (last > 1) add(last);
                    return pages.map((p, idx) => {
                      if (p === 'left-ellipsis' || p === 'right-ellipsis') {
                        return (
                          <span key={p} className="text-[#5C7599] px-2 select-none" data-testid={`pagination-${p}`}>…</span>
                        );
                      }
                      const isActive = p === page;
                      return (
                        <Button
                          key={p}
                          size="sm"
                          variant="ghost"
                          onClick={() => setPage(p)}
                          aria-current={isActive ? 'page' : undefined}
                          className={`min-w-[2.25rem] px-2 ${
                            isActive
                              ? 'bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/20'
                              : 'text-[#A0B4CC] hover:text-white hover:bg-white/5'
                          }`}
                          data-testid={`page-${p}-btn`}
                        >
                          {p}
                        </Button>
                      );
                    });
                  })()}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    className="text-[#A0B4CC] hover:text-white hover:bg-white/5 disabled:opacity-30"
                    data-testid="next-page-btn"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
          {user?.role !== 'merchant' && (
            <div className="mt-12 rounded-2xl border border-white/10 bg-[#041A3B] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="marketplace-merchant-cta">
              <div className="flex-1">
                <p className="text-white font-outfit font-semibold">Own a business? Get AGA members walking through your door.</p>
                <p className="text-sm text-[#A0B4CC] mt-1">List your business, set one member discount, and only give it when members pay.</p>
              </div>
              <Link to="/merchant/apply" className="rounded-full border border-[#00E676]/50 text-[#00E676] px-5 py-2 text-sm font-semibold hover:bg-[#00E676]/10 transition-colors text-center" data-testid="marketplace-merchant-cta-btn">Become a Partner Merchant</Link>
            </div>
          )}
        </div>
      </main>

      {/* Sticky mobile bottom CTA bar — Filters · Find Nearby · Search */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-[#000B18]/95 backdrop-blur-md border-t border-white/10 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2"
        data-testid="marketplace-mobile-bar"
      >
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setShowFilters(s => !s);
              // Scroll to filters area when opened
              if (!showFilters) setTimeout(() => window.scrollTo({ top: 200, behavior: 'smooth' }), 100);
            }}
            className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl border transition-all ${
              showFilters
                ? 'bg-[#00E5FF]/15 border-[#00E5FF]/40 text-[#00E5FF]'
                : 'bg-[#041A3B] border-white/10 text-white active:bg-white/5'
            }`}
            data-testid="mobile-bar-filters"
            aria-label="Open filters"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-[10px] font-medium">Filters</span>
            {activeFilterCount > 0 && (
              <span className="absolute top-1 right-3 min-w-[16px] h-4 px-1 rounded-full bg-[#00E5FF] text-[#000B18] text-[9px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          <button
            onClick={() => {
              try { navigator.vibrate?.(10); } catch (_) {/* ignore */}
              if (userLoc) clearLocation(); else requestLocation();
            }}
            disabled={locStatus === 'locating'}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl border transition-all active:scale-95 ${
              userLoc
                ? 'bg-[#00E676]/15 border-[#00E676]/40 text-[#00E676]'
                : 'bg-[#041A3B] border-white/10 text-white active:bg-white/5'
            }`}
            data-testid="mobile-bar-nearby"
            aria-label={userLoc ? 'Disable nearby' : 'Find nearby'}
          >
            {locStatus === 'locating'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Locate className="w-4 h-4" />
            }
            <span className="text-[10px] font-medium">{userLoc ? 'Nearby ON' : 'Nearby'}</span>
          </button>

          <button
            onClick={() => {
              try { navigator.vibrate?.(10); } catch (_) {/* ignore */}
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setTimeout(() => searchInputRef.current?.focus(), 350);
            }}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl border bg-[#041A3B] border-white/10 text-white active:bg-white/5 transition-all active:scale-95"
            data-testid="mobile-bar-search"
            aria-label="Focus search"
          >
            <Search className="w-4 h-4" />
            <span className="text-[10px] font-medium">Search</span>
          </button>
        </div>
      </div>

    </MarketplaceShell>
  );
}

/**
 * Renders the marketplace inside the member sidebar for logged-in users
 * (per spec: transactions/marketplace live inside the dashboard) and inside
 * the classic public Header/Footer chrome for guests.
 */
function MarketplaceShell({ children }) {
  const { user } = useAuth();
  if (user) {
    return <MemberSidebarLayout>{children}</MemberSidebarLayout>;
  }
  return (
    <div className="min-h-screen bg-[#000B18]">
      <Header />
      {children}
      <Footer />
    </div>
  );
}

function fmtDistance(km) {
  if (km == null) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  if (km > 500) return 'Far';
  return `${Math.round(km)} km`;
}

function EmptyState({ search, title, icon: Icon }) {
  return (
    <div className="text-center py-16 sm:py-20" data-testid="marketplace-empty">
      <Icon className="w-14 h-14 sm:w-16 sm:h-16 text-[#5C7599]/30 mx-auto mb-4" />
      <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-[#A0B4CC] text-sm sm:text-base px-4">
        {search ? 'Try a different search term or adjust your filters.' : 'Check back soon for new listings!'}
      </p>
    </div>
  );
}
